import React, { useState, useEffect } from 'react';
import { useCreateProject, useUpdateProject } from '../../hooks/useProjects';
import { useAuth } from '../../contexts/AuthContext';
import ClientSelector from '../clients/ClientSelector';
import type { Project, ProjectCreate, ProjectUpdate } from '../../types';

interface ProjectFormProps {
  project?: Project;
  onSuccess: () => void;
  onCancel: () => void;
}

const ProjectForm: React.FC<ProjectFormProps> = ({ project, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    budget: '',
    dueDate: '',
    clientId: null as string | null,
    orderNumber: '',
  });

  const createProject = useCreateProject();
  const updateProject = useUpdateProject();

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name,
        description: project.description || '',
        budget: project.budget.toString(),
        dueDate: project.dueDate || '',
        clientId: project.clientId ?? null,
        orderNumber: project.orderNumber || '',
      });
    }
  }, [project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) return;

    try {
      if (project) {
        const data: ProjectUpdate = {
          name: formData.name,
          description: formData.description || undefined,
          budget: parseFloat(formData.budget),
          dueDate: formData.dueDate || undefined,
          clientId: formData.clientId,
          orderNumber: formData.orderNumber || undefined,
        };
        await updateProject.mutateAsync({
          projectId: project.projectId,
          data,
        });
      } else {
        const data: ProjectCreate = {
          name: formData.name,
          description: formData.description || undefined,
          budget: parseFloat(formData.budget),
          responsibleUserId: user.id,
          dueDate: formData.dueDate || undefined,
          clientId: formData.clientId,
          orderNumber: formData.orderNumber || undefined,
        };
        await createProject.mutateAsync(data);
      }
      onSuccess();
    } catch (error) {
      // Error handling is done in the hooks
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isLoading = createProject.isLoading || updateProject.isLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Nombre del Proyecto *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Ej: Construcción Edificio A"
        />
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
          Descripción
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Descripción del proyecto..."
        />
      </div>

      {/* Client */}
      <div>
        <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-2">
          Cliente
        </label>
        <ClientSelector
          value={formData.clientId}
          onChange={(clientId) => setFormData((prev) => ({ ...prev, clientId }))}
          placeholder="Sin cliente asignado"
        />
        <p className="mt-1 text-xs text-gray-500">
          Opcional. Asocia este proyecto a un cliente de tu empresa.
        </p>
      </div>

      {/* Order Number */}
      <div>
        <label htmlFor="orderNumber" className="block text-sm font-medium text-gray-700 mb-2">
          Número de Orden
        </label>
        <input
          type="text"
          id="orderNumber"
          name="orderNumber"
          value={formData.orderNumber}
          onChange={handleChange}
          maxLength={100}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Ej: OC-2026-001"
        />
        <p className="mt-1 text-xs text-gray-500">
          Opcional. Número de orden de compra del cliente para este proyecto.
        </p>
      </div>

      {/* Budget */}
      <div>
        <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
          Presupuesto (CLP) *
        </label>
        <input
          type="number"
          id="budget"
          name="budget"
          value={formData.budget}
          onChange={handleChange}
          required
          min="0"
          step="0.01"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="0.00"
        />
      </div>

      {/* Due Date */}
      <div>
        <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-2">
          Fecha de Vencimiento
        </label>
        <input
          type="date"
          id="dueDate"
          name="dueDate"
          value={formData.dueDate}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500">
          Opcional. Se mostrará un indicador de color según la proximidad a esta fecha.
        </p>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {isLoading ? 'Guardando...' : project ? 'Actualizar' : 'Crear Proyecto'}
        </button>
      </div>
    </form>
  );
};

export default ProjectForm;
